import RequireOwner from "@/components/RequireOwner";
import { useOnboarding } from "@/components/owner/OnboardingProvider";
import { useOwnerAuth } from "@/components/owner/OwnerAuthProvider";
import StepHeader from "@/components/owner/StepHeader";
import {
  getFriendlyImageUploadError,
  isImageUploadCancelled,
  type ImageUploadProgress,
} from "@/lib/imageUpload";
import { pickAndUploadBrandImage } from "@/lib/media/businessImages";
import {
  patchBusiness,
  persistOwnerOnboardingStep,
  readBusiness,
} from "@/lib/onboardingBusiness";
import { resolveOwnerOnboardingAccess } from "@/lib/onboardingResume";
import { validateBrand } from "@/lib/validation";
import {
  WHATSAPP_CATALOG_CTA_LABEL,
  WHATSAPP_CHAT_CTA_LABEL,
  normalizeWhatsAppChatNumber,
  normalizeWhatsAppCtaType,
  type WhatsAppCtaType,
} from "@/lib/whatsapp";
import { router, useLocalSearchParams } from "expo-router";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Image,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
  type KeyboardTypeOptions,
  type LayoutChangeEvent,
} from "react-native";
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view";
import { useSafeAreaInsets } from "react-native-safe-area-context";

// Match Basics' page ScrollView on web; retain keyboard-aware scrolling on mobile.
const BrandFormScrollView =
  Platform.OS === "web" ? ScrollView : KeyboardAwareScrollView;

type LogoKind = "logo" | "profile";
type LogoFit = "contain" | "cover";
type EditMode = "onboarding" | "edit-approved";

type BrandForm = {
  shortDescription: string;
  logoUrl: string;
  logoKind: LogoKind;
  logoFit: LogoFit;
  whatsappCtaType: WhatsAppCtaType;
  whatsappChatNumber: string;
  whatsappCatalogUrl: string;
  website: string;
  email: string;
  facebook: string;
  instagram: string;
  tiktok: string;
  youtube: string;
  twitter: string;
};

type BrandStringField =
  | "shortDescription"
  | "logoUrl"
  | "whatsappChatNumber"
  | "whatsappCatalogUrl"
  | "website"
  | "email"
  | "facebook"
  | "instagram"
  | "tiktok"
  | "youtube"
  | "twitter";

type BrandFieldKey =
  | "logoUrl"
  | "whatsappChatNumber"
  | "whatsappCatalogUrl"
  | "social";

type BrandFieldErrors = Partial<Record<BrandFieldKey, string>>;

const BRAND_ERROR_ORDER: BrandFieldKey[] = [
  "logoUrl",
  "whatsappChatNumber",
  "whatsappCatalogUrl",
  "social",
];

const FIX_HIGHLIGHTED_FIELDS_MESSAGE =
  "Please fix the highlighted fields before continuing.";

type BrandBusinessDoc = {
  id?: string;
  status?: unknown;
  isPublished?: unknown;
  isDeleted?: unknown;
  isDeactivated?: unknown;

  logoUrl?: unknown;
  logoKind?: unknown;
  logoFit?: unknown;

  whatsappCtaType?: unknown;
  whatsappChatNumber?: unknown;
  whatsappCatalogUrl?: unknown;

  website?: unknown;
  websiteUrl?: unknown;

  email?: unknown;
  businessEmail?: unknown;

  facebook?: unknown;
  facebookUrl?: unknown;

  instagram?: unknown;
  instagramUrl?: unknown;

  tiktok?: unknown;
  tiktokUrl?: unknown;

  youtube?: unknown;
  youtubeUrl?: unknown;

  twitter?: unknown;
  x?: unknown;
  xUrl?: unknown;
  twitterUrl?: unknown;

  shortDescription?: unknown;
};

const MAX_SHORT_DESCRIPTION_LENGTH = 500;

function getMode(value: unknown): EditMode {
  return value === "edit-approved" ? "edit-approved" : "onboarding";
}

function readString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function readLogoKind(value: unknown): LogoKind {
  return value === "profile" || value === "logo" ? value : "logo";
}

function readLogoFit(value: unknown, logoKind: LogoKind): LogoFit {
  if (value === "cover" || value === "contain") return value;
  return logoKind === "profile" ? "cover" : "contain";
}

function useDebouncedPatch(
  fn: (patch: Record<string, unknown>) => Promise<void> | void,
  delay = 400
) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (patch: Record<string, unknown>) => {
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      void Promise.resolve(fn(patch)).catch(() => {
        console.warn("Brand auto-save failed.");
      });
    }, delay);
  };
}

function buildSafeApprovedBrandPatch(patch: Record<string, unknown>) {
  const allowed = new Set([
    "shortDescription",
    "logoUrl",
    "logoKind",
    "logoFit",
    "coverUrl",
    "whatsappCtaType",
    "whatsappChatNumber",
    "whatsappCatalogUrl",
    "website",
    "websiteUrl",
    "email",
    "businessEmail",
    "facebook",
    "facebookUrl",
    "instagram",
    "instagramUrl",
    "tiktok",
    "tiktokUrl",
    "youtube",
    "youtubeUrl",
    "twitter",
    "twitterUrl",
    "x",
    "xUrl",
  ]);

  const safe: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(patch)) {
    if (allowed.has(key)) {
      safe[key] = value;
    }
  }

  return safe;
}

const initialForm: BrandForm = {
  shortDescription: "",
  logoUrl: "",
  logoKind: "logo",
  logoFit: "contain",
  whatsappCtaType: "chat",
  whatsappChatNumber: "",
  whatsappCatalogUrl: "",
  website: "",
  email: "",
  facebook: "",
  instagram: "",
  tiktok: "",
  youtube: "",
  twitter: "",
};

function getBrandFieldErrors(
  form: BrandForm,
  requireFullOnboardingFields: boolean
): BrandFieldErrors {
  const validation = validateBrand(form);
  const errors: BrandFieldErrors = {};

  if (requireFullOnboardingFields && validation.errors.logoUrl) {
    errors.logoUrl = "Logo or profile photo is required.";
  }

  if (form.whatsappCtaType === "chat") {
    if (validation.errors.whatsappChatNumberMissing) {
      errors.whatsappChatNumber =
        "WhatsApp chat number is required when the chat CTA is selected.";
    } else if (validation.errors.whatsappChatNumberInvalid) {
      errors.whatsappChatNumber =
        "Enter a valid WhatsApp number, for example +2547XXXXXXXX.";
    }
  }

  if (form.whatsappCtaType === "catalog") {
    if (validation.errors.whatsappCatalogUrlMissing) {
      errors.whatsappCatalogUrl =
        "WhatsApp catalog URL is required when the catalog CTA is selected.";
    } else if (validation.errors.whatsappCatalogUrlInvalid) {
      errors.whatsappCatalogUrl =
        "Enter a valid WhatsApp catalog URL, for example https://wa.me/c/254xxxxxxxx.";
    }
  }

  if (requireFullOnboardingFields && validation.errors.social) {
    errors.social = "Add at least one website, email, or social link.";
  }

  return errors;
}

export default function Brand() {
  const { user } = useOwnerAuth();
  const { businessId, setBusinessId, correctionAuthorized } = useOnboarding();
  const insets = useSafeAreaInsets();

  const params = useLocalSearchParams<{
    businessId?: string;
    mode?: string;
  }>();

  const mode = getMode(params.mode);
  const isApprovedEditMode = mode === "edit-approved";

  const paramBusinessId =
    typeof params.businessId === "string" ? params.businessId : undefined;

  const effectiveBusinessId = paramBusinessId || businessId || null;

  const [loading, setLoading] = useState(true);
  const [busyImage, setBusyImage] = useState(false);
  const [imageUploadStatus, setImageUploadStatus] = useState<string | null>(
    null
  );
  const [imageUploadPercent, setImageUploadPercent] = useState<number | null>(
    null
  );
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<BrandFieldErrors>({});
  const [form, setForm] = useState<BrandForm>(initialForm);

  const businessIdRef = useRef<string | null>(null);
  const scrollRef = useRef<any>(null);
  const fieldPositionsRef = useRef<Partial<Record<BrandFieldKey, number>>>({});
  const fieldInputRefs = useRef<Partial<Record<BrandFieldKey, TextInput | null>>>(
    {}
  );

  useEffect(() => {
    businessIdRef.current = effectiveBusinessId;
  }, [effectiveBusinessId]);

  const hasVisibleFieldErrors = useMemo(
    () => Object.values(fieldErrors).some(Boolean),
    [fieldErrors]
  );

  const registerFieldLayout = (key: BrandFieldKey) => (
    event: LayoutChangeEvent
  ) => {
    fieldPositionsRef.current[key] = event.nativeEvent.layout.y;
  };

  const registerFieldInput = (key: BrandFieldKey) => (
    node: TextInput | null
  ) => {
    fieldInputRefs.current[key] = node;
  };

  const clearFieldError = (key: BrandFieldKey) => {
    setFieldErrors((previous) => {
      if (!previous[key]) return previous;

      const next = { ...previous };
      delete next[key];
      return next;
    });
  };

  const focusFirstInvalidField = (errors: BrandFieldErrors) => {
    const firstKey = BRAND_ERROR_ORDER.find((key) => !!errors[key]);

    if (!firstKey) return;

    const y = fieldPositionsRef.current[firstKey];

    if (typeof y === "number") {
      if (typeof scrollRef.current?.scrollToPosition === "function") {
        scrollRef.current.scrollToPosition(0, Math.max(y - 18, 0), true);
      } else {
        scrollRef.current?.scrollTo?.({
          y: Math.max(y - 18, 0),
          animated: true,
        });
      }
    }

    setTimeout(() => {
      fieldInputRefs.current[firstKey]?.focus?.();
    }, 250);
  };

  const showFieldErrors = (errors: BrandFieldErrors) => {
    setFieldErrors(errors);
    const focus = () => focusFirstInvalidField(errors);

    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(focus);
    } else {
      setTimeout(focus, 0);
    }
  };

  useEffect(() => {
    if (!user) return;
    if (!paramBusinessId) return;
    if (businessId === paramBusinessId) return;

    setBusinessId(paramBusinessId);
  }, [user, paramBusinessId, businessId, setBusinessId]);

  useEffect(() => {
    if (!user) {
      router.replace("/(owner)/onboarding/signin" as any);
      return;
    }

    if (!effectiveBusinessId) {
      router.replace(
        isApprovedEditMode
          ? ("/(owner)/dashboard" as any)
          : ("/(owner)/onboarding/basics" as any)
      );
    }
  }, [user, effectiveBusinessId, isApprovedEditMode]);

  useEffect(() => {
    if (!user || !effectiveBusinessId) return;

    const currentBusinessId = effectiveBusinessId;
    let mounted = true;

    async function loadBrand() {
      try {
        const data = (await readBusiness(
          currentBusinessId
        )) as BrandBusinessDoc | null | undefined;

        if (!mounted) return;

        const onboardingAccess = resolveOwnerOnboardingAccess({
          status: data?.status,
          isPublished: data?.isPublished === true,
          isDeleted: data?.isDeleted === true,
          approvedEditMode: isApprovedEditMode,
          correctionAuthorized,
        });

        if (onboardingAccess === "review") {
          router.replace({
            pathname: "/(owner)/onboarding/review",
            params: { businessId: currentBusinessId },
          } as any);
          return;
        }

        if (onboardingAccess === "dashboard") {
          router.replace("/(owner)/dashboard" as any);
          return;
        }

        const logoKind = readLogoKind(data?.logoKind);
        const logoFit = readLogoFit(data?.logoFit, logoKind);

        setForm({
          shortDescription: readString(data?.shortDescription).slice(
            0,
            MAX_SHORT_DESCRIPTION_LENGTH
          ),
          logoUrl: readString(data?.logoUrl),
          logoKind,
          logoFit,
          whatsappCtaType: normalizeWhatsAppCtaType(data?.whatsappCtaType),
          whatsappChatNumber: readString(data?.whatsappChatNumber),
          whatsappCatalogUrl: readString(data?.whatsappCatalogUrl),
          website: readString(data?.website) || readString(data?.websiteUrl),
          email: readString(data?.email) || readString(data?.businessEmail),
          facebook: readString(data?.facebook) || readString(data?.facebookUrl),
          instagram:
            readString(data?.instagram) || readString(data?.instagramUrl),
          tiktok: readString(data?.tiktok) || readString(data?.tiktokUrl),
          youtube: readString(data?.youtube) || readString(data?.youtubeUrl),
          twitter:
            readString(data?.twitter) ||
            readString(data?.x) ||
            readString(data?.xUrl) ||
            readString(data?.twitterUrl),
        });
      } catch {
        console.warn("Brand onboarding load failed.");

        if (mounted) {
          Alert.alert(
            "Could not load brand details",
            "Please go back to Basics and try again."
          );
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    void loadBrand();

    return () => {
      mounted = false;
    };
  }, [user, effectiveBusinessId, isApprovedEditMode, correctionAuthorized]);

  const doPatch = useDebouncedPatch(async (patch) => {
    const id = businessIdRef.current;
    if (!id) return;

    const finalPatch = isApprovedEditMode
      ? buildSafeApprovedBrandPatch(patch)
      : patch;

    if (Object.keys(finalPatch).length === 0) {
      return;
    }

    await patchBusiness(id, finalPatch);
  }, 500);

  const setField = (key: BrandStringField, value: string) => {
    if (submitError) setSubmitError(null);

    const nextValue =
      key === "shortDescription"
        ? value.slice(0, MAX_SHORT_DESCRIPTION_LENGTH)
        : value;
    const nextForm: BrandForm = { ...form, [key]: nextValue };

    setForm((previous) => ({ ...previous, [key]: nextValue }));

    if (key === "whatsappChatNumber") {
      const nextErrors = getBrandFieldErrors(nextForm, !isApprovedEditMode);
      if (!nextErrors.whatsappChatNumber) {
        clearFieldError("whatsappChatNumber");
      }
    }

    if (key === "whatsappCatalogUrl") {
      const nextErrors = getBrandFieldErrors(nextForm, !isApprovedEditMode);
      if (!nextErrors.whatsappCatalogUrl) {
        clearFieldError("whatsappCatalogUrl");
      }
    }

    if (
      key === "website" ||
      key === "email" ||
      key === "facebook" ||
      key === "instagram" ||
      key === "tiktok" ||
      key === "youtube" ||
      key === "twitter"
    ) {
      const nextErrors = getBrandFieldErrors(nextForm, !isApprovedEditMode);
      if (!nextErrors.social) {
        clearFieldError("social");
      }
    }

    if (key === "whatsappChatNumber") {
      const normalized = normalizeWhatsAppChatNumber(nextValue);

      if (normalized || !nextValue.trim()) {
        doPatch({ whatsappChatNumber: normalized });
      }

      return;
    }

    doPatch({ [key]: nextValue });
  };

  const setWhatsAppCtaType = (whatsappCtaType: WhatsAppCtaType) => {
    if (submitError) setSubmitError(null);

    setForm((previous) => ({ ...previous, whatsappCtaType }));
    clearFieldError("whatsappChatNumber");
    clearFieldError("whatsappCatalogUrl");
    doPatch({ whatsappCtaType });
  };

  const setBrandImageKind = (logoKind: LogoKind) => {
    const logoFit = logoKind === "profile" ? "cover" : "contain";

    setForm((previous) => ({
      ...previous,
      logoKind,
      logoFit,
    }));

    doPatch({ logoKind, logoFit });
  };

  const handleImageUploadProgress = (progress: ImageUploadProgress) => {
    setImageUploadStatus(progress.message);
    setImageUploadPercent(
      typeof progress.percent === "number" ? progress.percent : null
    );
  };

  const pickBrandIdentityImage = async () => {
    if (busyImage) return;

    const id = businessIdRef.current;

    if (!id) {
      Alert.alert(
        "Missing businessId",
        "Please go back to Basics and try again."
      );
      return;
    }

    if (!user) {
      Alert.alert("Not signed in", "Please sign in again and try.");
      return;
    }

    setBusyImage(true);
    setImageUploadStatus("Preparing image...");
    setImageUploadPercent(null);
    setImageUploadError(null);

    try {
      const selectedKind = form.logoKind;
      const uploaded = await pickAndUploadBrandImage({
        businessId: id,
        kind: selectedKind,
        onProgress: handleImageUploadProgress,
      });

      setForm((previous) => ({
        ...previous,
        logoUrl: uploaded.url,
        logoKind: selectedKind,
        logoFit: uploaded.fit,
      }));

      clearFieldError("logoUrl");
      setSubmitError(null);
      setImageUploadStatus("Image uploaded successfully.");
      setImageUploadPercent(100);

      doPatch({
        logoUrl: uploaded.url,
        logoKind: selectedKind,
        logoFit: uploaded.fit,
      });
    } catch (error: unknown) {
      if (isImageUploadCancelled(error)) {
        setImageUploadStatus(null);
        setImageUploadPercent(null);
        return;
      }

      const message = getFriendlyImageUploadError(error);

      setImageUploadStatus(null);
      setImageUploadPercent(null);
      setImageUploadError(message);
      Alert.alert("Upload failed", message);
    } finally {
      setBusyImage(false);
    }
  };

  const removeBrandIdentityImage = () => {
    setForm((previous) => ({
      ...previous,
      logoUrl: "",
      logoKind: "logo",
      logoFit: "contain",
    }));
    setImageUploadStatus(null);
    setImageUploadPercent(null);
    setImageUploadError(null);

    doPatch({
      logoUrl: "",
      logoKind: "logo",
      logoFit: "contain",
    });
  };

  const imageResizeMode = form.logoFit === "cover" ? "cover" : "contain";
  const selectedImageLabel =
    form.logoKind === "profile" ? "profile photo" : "logo";
  const activeImageUploadLabel =
    typeof imageUploadPercent === "number" &&
    imageUploadStatus?.startsWith("Uploading image")
      ? `Uploading image... ${imageUploadPercent}%`
      : imageUploadStatus ?? "Preparing image...";
  const imageUploadFeedback = busyImage
    ? activeImageUploadLabel
    : imageUploadError
    ? imageUploadError
    : imageUploadStatus;

  const goBack = () => {
    const id = businessIdRef.current;

    router.push({
      pathname: "/(owner)/onboarding/basics",
      params: {
        ...(id ? { businessId: id } : {}),
        mode: isApprovedEditMode ? "edit-approved" : "onboarding",
      },
    } as any);
  };

  const goNext = async () => {
    if (saving || busyImage) return;

    setSubmitError(null);

    const nextErrors = getBrandFieldErrors(form, !isApprovedEditMode);

    if (Object.keys(nextErrors).length > 0) {
      setSubmitError(FIX_HIGHLIGHTED_FIELDS_MESSAGE);
      showFieldErrors(nextErrors);
      return;
    }

    if (hasVisibleFieldErrors) {
      setFieldErrors({});
    }

    setSaving(true);

    try {
      const id = businessIdRef.current;

      if (!id) {
        const message = "Please go back to Basics and try again.";
        setSubmitError(message);
        Alert.alert(
          "Missing businessId",
          message
        );
        return;
      }

      const safeLinksPayload = {
        shortDescription: form.shortDescription.trim(),
        logoUrl: form.logoUrl,
        logoKind: form.logoKind,
        logoFit: form.logoFit,
        whatsappCtaType: form.whatsappCtaType,
        whatsappChatNumber:
          normalizeWhatsAppChatNumber(form.whatsappChatNumber) ||
          form.whatsappChatNumber.trim(),
        whatsappCatalogUrl: form.whatsappCatalogUrl.trim(),
        website: form.website,
        email: form.email,
        facebook: form.facebook,
        instagram: form.instagram,
        tiktok: form.tiktok,
        youtube: form.youtube,
        twitter: form.twitter,
      };

      if (isApprovedEditMode) {
        await patchBusiness(id, safeLinksPayload);

        router.push({
          pathname: "/(owner)/onboarding/items",
          params: {
            businessId: id,
            mode: "edit-approved",
          },
        } as any);

        return;
      }

      await patchBusiness(id, safeLinksPayload);
      await persistOwnerOnboardingStep("items");

      router.push({
        pathname: "/(owner)/onboarding/items",
        params: {
          businessId: id,
          mode: "onboarding",
        },
      } as any);
    } catch (error: any) {
      const message = error?.message || "Unknown error";
      setSubmitError(message);
      Alert.alert("Could not continue", message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <RequireOwner>
      {loading ? (
        <View style={loadingWrap}>
          <Text style={{ fontWeight: "900" }}>Loading...</Text>
        </View>
      ) : (
        <View style={screen}>
          <KeyboardAvoidingView
            style={{ flex: 1 }}
            behavior={
              Platform.OS === "ios"
                ? "padding"
                : Platform.OS === "android"
                ? "height"
                : undefined
            }
            keyboardVerticalOffset={Platform.OS === "android" ? 24 : 0}
          >
            <BrandFormScrollView
              ref={scrollRef}
              style={{ flex: 1 }}
              contentContainerStyle={{
                padding: 16,
                paddingBottom: 32,
                flexGrow: 1,
              }}
              {...(Platform.OS !== "web"
                ? {
                    enableOnAndroid: true,
                    extraScrollHeight: Platform.OS === "ios" ? 30 : 90,
                    extraHeight: Platform.OS === "ios" ? 120 : 200,
                  }
                : {})}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={Platform.OS === "web"}
            >
              <View style={container}>
                <View style={card}>
                  <StepHeader
                    title={
                      isApprovedEditMode
                        ? "Brand & Links"
                        : "Step 2: Brand"
                    }
                    subtitle={
                      isApprovedEditMode
                        ? "Update logo/profile photo, About Us, WhatsApp button, website, email, and social links."
                        : "Required - Logo / Photo + WhatsApp button + at least 1 website, email, or social link."
                    }
                    onBack={goBack}
                  />

                  {hasVisibleFieldErrors ? (
                    <View style={topErrorBox}>
                      <Text style={topErrorText}>
                        Please fix the highlighted fields before continuing.
                      </Text>
                    </View>
                  ) : null}

                  <Text style={sectionTitle}>Brand image *</Text>

                  <Text style={helperText}>
                    Choose a logo if you have an official business logo, or a
                    profile photo for a shopfront, product, or work photo.
                  </Text>

                  <View style={brandKindSelector}>
                    <Pressable
                      onPress={() => setBrandImageKind("logo")}
                      disabled={busyImage}
                      style={[
                        brandKindOption,
                        form.logoKind === "logo" ? brandKindOptionActive : null,
                        busyImage ? { opacity: 0.55 } : null,
                      ]}
                    >
                      <Text
                        style={[
                          brandKindTitle,
                          form.logoKind === "logo"
                            ? brandKindTitleActive
                            : null,
                        ]}
                      >
                        Logo
                      </Text>
                      <Text style={brandKindHelp}>
                        Best for official business logo
                      </Text>
                    </Pressable>

                    <Pressable
                      onPress={() => setBrandImageKind("profile")}
                      disabled={busyImage}
                      style={[
                        brandKindOption,
                        form.logoKind === "profile"
                          ? brandKindOptionActive
                          : null,
                        busyImage ? { opacity: 0.55 } : null,
                      ]}
                    >
                      <Text
                        style={[
                          brandKindTitle,
                          form.logoKind === "profile"
                            ? brandKindTitleActive
                            : null,
                        ]}
                      >
                        Profile / Shopfront
                      </Text>
                      <Text style={brandKindHelp}>
                        Best for shopfront, product, or work photo
                      </Text>
                    </Pressable>
                  </View>

                  <View
                    onLayout={registerFieldLayout("logoUrl")}
                    style={[
                      brandRow,
                      fieldErrors.logoUrl ? brandRowError : null,
                    ]}
                  >
                    <View style={previewOuter}>
                      {!!form.logoUrl ? (
                        <Image
                          source={{ uri: form.logoUrl }}
                          style={previewImage}
                          resizeMode={imageResizeMode}
                        />
                      ) : (
                        <View style={previewEmpty}>
                          <Text style={previewEmptyText}>No image</Text>
                        </View>
                      )}
                    </View>

                    <View style={previewActions}>
                      <Pressable
                        onPress={pickBrandIdentityImage}
                        disabled={busyImage}
                        style={[
                          sideButton,
                          busyImage ? { opacity: 0.45 } : null,
                        ]}
                      >
                        <Text style={sideButtonText}>
                          {busyImage
                            ? activeImageUploadLabel
                            : form.logoUrl
                            ? `Change ${selectedImageLabel}`
                            : `Upload ${selectedImageLabel}`}
                        </Text>
                      </Pressable>

                      <Pressable
                        onPress={removeBrandIdentityImage}
                        disabled={!form.logoUrl || busyImage}
                        style={[
                          sideButton,
                          !form.logoUrl || busyImage ? { opacity: 0.45 } : null,
                        ]}
                      >
                        <Text style={sideButtonTextDark}>Remove image</Text>
                      </Pressable>
                    </View>
                  </View>

                  {fieldErrors.logoUrl ? (
                    <Text style={errorText}>{fieldErrors.logoUrl}</Text>
                  ) : null}

                  {imageUploadFeedback ? (
                    <Text
                      style={[
                        imageUploadStatusText,
                        imageUploadError ? imageUploadStatusErrorText : null,
                      ]}
                    >
                      {imageUploadFeedback}
                    </Text>
                  ) : null}

                  <View style={{ height: 10 }} />

                  <Text style={sectionTitle}>About Us</Text>

                  <Field
                    label="About Us"
                    value={form.shortDescription}
                    onChange={(value) => setField("shortDescription", value)}
                    hint={`${form.shortDescription.length}/${MAX_SHORT_DESCRIPTION_LENGTH} characters`}
                    returnKeyType="done"
                    multiline
                    maxLength={MAX_SHORT_DESCRIPTION_LENGTH}
                  />

                  <Text style={sectionTitle}>WhatsApp button *</Text>

                  <View style={ctaTypeSelector}>
                    <Pressable
                      onPress={() => setWhatsAppCtaType("chat")}
                      style={[
                        ctaTypeOption,
                        form.whatsappCtaType === "chat"
                          ? ctaTypeOptionActive
                          : null,
                      ]}
                    >
                      <Text
                        style={[
                          ctaTypeTitle,
                          form.whatsappCtaType === "chat"
                            ? ctaTypeTitleActive
                            : null,
                        ]}
                      >
                        {WHATSAPP_CHAT_CTA_LABEL}
                      </Text>
                    </Pressable>

                    <Pressable
                      onPress={() => setWhatsAppCtaType("catalog")}
                      style={[
                        ctaTypeOption,
                        form.whatsappCtaType === "catalog"
                          ? ctaTypeOptionActive
                          : null,
                      ]}
                    >
                      <Text
                        style={[
                          ctaTypeTitle,
                          form.whatsappCtaType === "catalog"
                            ? ctaTypeTitleActive
                            : null,
                        ]}
                      >
                        {WHATSAPP_CATALOG_CTA_LABEL}
                      </Text>
                    </Pressable>
                  </View>

                  {form.whatsappCtaType === "chat" ? (
                    <Field
                      label="WhatsApp chat number"
                      value={form.whatsappChatNumber}
                      onChange={(value) =>
                        setField("whatsappChatNumber", value)
                      }
                      hint="Example: +2547XXXXXXXX"
                      error={fieldErrors.whatsappChatNumber}
                      keyboardType="phone-pad"
                      returnKeyType="next"
                      inputRef={registerFieldInput("whatsappChatNumber")}
                      onLayout={registerFieldLayout("whatsappChatNumber")}
                    />
                  ) : null}

                  {form.whatsappCtaType === "catalog" ? (
                    <Field
                      label="WhatsApp Catalog URL"
                      value={form.whatsappCatalogUrl}
                      onChange={(value) =>
                        setField("whatsappCatalogUrl", value)
                      }
                      hint="Example: https://wa.me/c/254xxxxxxxx"
                      error={fieldErrors.whatsappCatalogUrl}
                      keyboardType="url"
                      returnKeyType="next"
                      inputRef={registerFieldInput("whatsappCatalogUrl")}
                      onLayout={registerFieldLayout("whatsappCatalogUrl")}
                    />
                  ) : null}

                  <View
                    onLayout={registerFieldLayout("social")}
                    style={fieldErrors.social ? socialGroupError : null}
                  >
                    <Text style={sectionTitle}>
                      Website, email, or social links *
                    </Text>

                    <Field
                      label="Website"
                      value={form.website}
                      onChange={(value) => setField("website", value)}
                      keyboardType="url"
                      returnKeyType="next"
                      hint="Example: sokogrid.com"
                      inputRef={registerFieldInput("social")}
                    />

                    <Field
                      label="Email"
                      value={form.email}
                      onChange={(value) => setField("email", value)}
                      keyboardType="email-address"
                      returnKeyType="next"
                      hint="Example: info@yourbusiness.com"
                    />

                    <Field
                      label="Facebook"
                      value={form.facebook}
                      onChange={(value) => setField("facebook", value)}
                      keyboardType="url"
                      returnKeyType="next"
                    />

                    <Field
                      label="Instagram"
                      value={form.instagram}
                      onChange={(value) => setField("instagram", value)}
                      keyboardType="url"
                      returnKeyType="next"
                    />

                    <Field
                      label="TikTok"
                      value={form.tiktok}
                      onChange={(value) => setField("tiktok", value)}
                      keyboardType="url"
                      returnKeyType="next"
                      hint="Example: @yourbusiness"
                    />

                    <Field
                      label="YouTube"
                      value={form.youtube}
                      onChange={(value) => setField("youtube", value)}
                      keyboardType="url"
                      returnKeyType="next"
                      hint="Example: @yourchannel"
                    />

                    <Field
                      label="Twitter/X"
                      value={form.twitter}
                      onChange={(value) => setField("twitter", value)}
                      keyboardType="url"
                      returnKeyType="done"
                    />

                    {fieldErrors.social ? (
                      <Text style={errorText}>{fieldErrors.social}</Text>
                    ) : null}
                  </View>
                </View>
              </View>
            </BrandFormScrollView>

            <View
              style={[
                footer,
                {
                  paddingBottom: Math.max(insets.bottom, 16),
                },
              ]}
            >
              <View style={footerInnerOne}>
                {submitError ? (
                  <Text style={footerErrorText}>{submitError}</Text>
                ) : null}

                <Pressable
                  onPress={goNext}
                  disabled={saving || busyImage}
                  style={[
                    btnPrimary,
                    saving || busyImage ? { opacity: 0.45 } : null,
                  ]}
                >
                  <Text style={btnPrimaryText}>
                    {saving
                      ? "Saving..."
                      : isApprovedEditMode
                      ? "Save & Next: Items →"
                      : "Next: Products/Services →"}
                  </Text>
                </Pressable>
              </View>
            </View>
          </KeyboardAvoidingView>
        </View>
      )}
    </RequireOwner>
  );
}

function Field({
  label,
  value,
  onChange,
  hint,
  error,
  keyboardType,
  returnKeyType,
  multiline,
  maxLength,
  inputRef,
  onLayout,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  error?: string;
  keyboardType?: KeyboardTypeOptions;
  returnKeyType?: "done" | "next";
  multiline?: boolean;
  maxLength?: number;
  inputRef?: (node: TextInput | null) => void;
  onLayout?: (event: LayoutChangeEvent) => void;
}) {
  return (
    <View style={{ marginBottom: 10 }} onLayout={onLayout}>
      <Text style={fieldLabel}>{label}</Text>

      <TextInput
        ref={inputRef}
        value={value}
        onChangeText={onChange}
        style={[
          input,
          multiline ? multilineInput : null,
          error ? inputError : null,
        ]}
        autoCapitalize="none"
        autoCorrect={false}
        keyboardType={keyboardType ?? "default"}
        returnKeyType={returnKeyType ?? "done"}
        blurOnSubmit={!multiline && returnKeyType !== "next"}
        multiline={multiline}
        maxLength={maxLength}
        textAlignVertical={multiline ? "top" : "center"}
      />

      {!!hint && !error ? <Text style={hintText}>{hint}</Text> : null}
      {!!error ? <Text style={errorText}>{error}</Text> : null}
    </View>
  );
}

const screen = {
  flex: 1,
  backgroundColor: "#F3F4F6",
} as const;

const loadingWrap = {
  flex: 1,
  justifyContent: "center" as const,
  alignItems: "center" as const,
  backgroundColor: "#F3F4F6",
} as const;

const container = {
  width: "100%",
  alignSelf: "center" as const,
  maxWidth: 760,
} as const;

const card = {
  backgroundColor: "#FFFFFF",
  borderRadius: 20,
  padding: 16,
  borderWidth: 1,
  borderColor: "#E5E7EB",
} as const;

const sectionTitle = {
  fontSize: 13,
  fontWeight: "900" as const,
  marginTop: 6,
  marginBottom: 6,
  color: "#111827",
} as const;

const helperText = {
  marginBottom: 12,
  fontSize: 12,
  lineHeight: 18,
  fontWeight: "700" as const,
  color: "#6B7280",
} as const;

const topErrorBox = {
  marginTop: 12,
  marginBottom: 10,
  borderRadius: 14,
  borderWidth: 1,
  borderColor: "#FCA5A5",
  backgroundColor: "#FEF2F2",
  padding: 12,
} as const;

const topErrorText = {
  color: "#991B1B",
  fontSize: 12,
  lineHeight: 18,
  fontWeight: "900" as const,
} as const;

const brandKindSelector = {
  flexDirection: "row" as const,
  gap: 10,
  marginBottom: 12,
} as const;

const brandKindOption = {
  flex: 1,
  minHeight: 76,
  borderRadius: 14,
  borderWidth: 1,
  borderColor: "#E5E7EB",
  backgroundColor: "#FFFFFF",
  padding: 12,
  justifyContent: "center" as const,
} as const;

const brandKindOptionActive = {
  borderColor: "#111827",
  backgroundColor: "#F9FAFB",
} as const;

const brandKindTitle = {
  color: "#374151",
  fontSize: 13,
  fontWeight: "900" as const,
  marginBottom: 4,
} as const;

const brandKindTitleActive = {
  color: "#111827",
} as const;

const brandKindHelp = {
  color: "#6B7280",
  fontSize: 11,
  fontWeight: "700" as const,
  lineHeight: 15,
} as const;

const ctaTypeSelector = {
  flexDirection: "row" as const,
  gap: 10,
  marginBottom: 10,
} as const;

const ctaTypeOption = {
  flex: 1,
  minHeight: 58,
  borderRadius: 14,
  borderWidth: 1,
  borderColor: "#E5E7EB",
  backgroundColor: "#FFFFFF",
  paddingHorizontal: 10,
  alignItems: "center" as const,
  justifyContent: "center" as const,
} as const;

const ctaTypeOptionActive = {
  borderColor: "#16A34A",
  backgroundColor: "#F0FDF4",
} as const;

const ctaTypeTitle = {
  color: "#374151",
  fontSize: 12,
  lineHeight: 16,
  fontWeight: "900" as const,
  textAlign: "center" as const,
} as const;

const ctaTypeTitleActive = {
  color: "#166534",
} as const;

const brandRow = {
  flexDirection: "row" as const,
  alignItems: "flex-start" as const,
  gap: 12,
  marginBottom: 4,
} as const;

const brandRowError = {
  padding: 8,
  borderRadius: 16,
  borderWidth: 1,
  borderColor: "#EF4444",
} as const;

const socialGroupError = {
  marginTop: 4,
  borderRadius: 16,
  borderWidth: 1,
  borderColor: "#EF4444",
  padding: 10,
} as const;

const previewOuter = {
  width: 110,
  height: 110,
  borderRadius: 22,
  borderWidth: 1,
  borderColor: "#E5E7EB",
  backgroundColor: "#F8FAFC",
  overflow: "hidden" as const,
  alignItems: "center" as const,
  justifyContent: "center" as const,
  flexShrink: 0,
} as const;

const previewImage = {
  width: "100%",
  height: "100%",
} as const;

const previewEmpty = {
  flex: 1,
  width: "100%",
  alignItems: "center" as const,
  justifyContent: "center" as const,
  paddingHorizontal: 8,
} as const;

const previewEmptyText = {
  fontSize: 12,
  fontWeight: "800" as const,
  color: "#9CA3AF",
} as const;

const previewActions = {
  flex: 1,
  gap: 10,
} as const;

const sideButton = {
  minHeight: 52,
  borderRadius: 18,
  borderWidth: 1,
  borderColor: "#E5E7EB",
  backgroundColor: "#FFFFFF",
  paddingHorizontal: 16,
  alignItems: "center" as const,
  justifyContent: "center" as const,
} as const;

const sideButtonText = {
  color: "#111827",
  fontWeight: "900" as const,
  fontSize: 14,
} as const;

const sideButtonTextDark = {
  color: "#111827",
  fontWeight: "900" as const,
  fontSize: 14,
} as const;

const fieldLabel = {
  fontSize: 12,
  fontWeight: "900" as const,
  color: "#374151",
  marginBottom: 6,
} as const;

const input = {
  height: 46,
  borderRadius: 14,
  borderWidth: 1,
  borderColor: "#E5E7EB",
  paddingHorizontal: 12,
  backgroundColor: "#FFFFFF",
  fontWeight: "800" as const,
} as const;

const inputError = {
  borderColor: "#EF4444",
} as const;

const multilineInput = {
  minHeight: 112,
  height: 112,
  paddingTop: 12,
  paddingBottom: 12,
  lineHeight: 20,
} as const;

const hintText = {
  marginTop: 4,
  fontSize: 11,
  fontWeight: "800" as const,
  color: "#6B7280",
} as const;

const errorText = {
  marginTop: 6,
  fontSize: 11,
  fontWeight: "900" as const,
  color: "#EF4444",
} as const;

const imageUploadStatusText = {
  marginTop: 6,
  fontSize: 11,
  lineHeight: 16,
  fontWeight: "900" as const,
  color: "#4B5563",
} as const;

const imageUploadStatusErrorText = {
  color: "#B91C1C",
} as const;

const footerErrorText = {
  color: "#B91C1C",
  fontSize: 13,
  lineHeight: 18,
  fontWeight: "800" as const,
  marginBottom: 10,
  textAlign: "center" as const,
} as const;

const footer = {
  paddingHorizontal: 16,
  paddingTop: 12,
  backgroundColor: "#FFFFFF",
  borderTopWidth: StyleSheet.hairlineWidth,
  borderTopColor: "#E5E7EB",
} as const;

const footerInnerOne = {
  width: "100%",
  alignSelf: "center" as const,
  maxWidth: 760,
} as const;

const btnPrimary = {
  height: 44,
  borderRadius: 12,
  alignItems: "center" as const,
  justifyContent: "center" as const,
  backgroundColor: "#0B1220",
} as const;

const btnPrimaryText = {
  color: "#FFFFFF",
  fontWeight: "900" as const,
} as const;
